/**
 * Registry centralizzato per le Static List
 *
 * Formato: identificativo -> configurazione
 * - Se `endpoint` presente: carica da API
 * - Se solo `fallback` presente: usa dati statici
 */

import type { StaticListItem, StaticListConfig } from "@/core/api/types/staticList";

// Opzioni statiche predefinite
export const STATIC_OPTIONS = {
  GENERE: [
    { id: 1, name: "Maschio", value: "M" },
    { id: 2, name: "Femmina", value: "F" },
  ] as StaticListItem[],
  TIPO_LEZIONE: [
    { id: 1, name: "Teoria", value: "Teoria" },
    { id: 2, name: "Pratica", value: "Pratica" },
  ] as StaticListItem[],
};

/**
 * Registry principale delle static list
 *
 * Per aggiungere una nuova lista:
 * 1. Se ha endpoint API: { endpoint: "/static_list/nome_lista" }
 * 2. Se statica: { fallback: [...] }
 * 3. Se in migrazione: { endpoint: "...", fallback: [...] } (fallback usato se API fallisce)
 */
export const staticListRegistry: Record<string, StaticListConfig> = {
  // === LISTE API v2 ===
  tipologie_corsi: {
    endpoint: "/static_list/tipologie_corsi",
    queryKey: "tipologie_corsi",
  },
  categorie_corsi: {
    endpoint: "/static_list/categorie_corsi",
    queryKey: "categorie_corsi",
  },
  tipologia_documento: {
    endpoint: "/static_list/tipologia_documento",
    queryKey: "tipologia_documento",
  },
  soggetto_attuatore: {
    endpoint: "/static_list/soggetto_attuatore",
    queryKey: "soggetto_attuatore",
  },
  attori_didattici_tipologia: {
    endpoint: "/static_list/attori_didattici_tipologia",
    queryKey: "attori_didattici_tipologia",
  },
  stato_corso: {
    endpoint: "/static_list/stato_corso",
    queryKey: "stato_corso",
  },
  tipo_campi: {
    endpoint: "/static_list/tipo_campi",
    queryKey: "tipo_campi",
  },
  classe_corso: {
    endpoint: "/static_list/classe_corso",
    queryKey: "classe_corso",
  },
  campi_personalizzati: {
    endpoint: "/static_list/campi_personalizzati",
    queryKey: "campi_personalizzati",
  },
  template_attestati: {
    endpoint: "/static_list/template_attestati",
    queryKey: "template_attestati",
  },

  // === LISTE STATICHE (solo fallback, no API) ===
  genere: {
    fallback: STATIC_OPTIONS.GENERE,
  },
  tipo_lezione: {
    fallback: STATIC_OPTIONS.TIPO_LEZIONE,
  },
  /** Enti (dropdown modale categoria corso — statica finché non esiste endpoint dedicato) */
  enti: {
    fallback: [
      { id: "1", name: "OPN sicilia" },
      { id: "2", name: "Ente formativo Centro Italia" },
      { id: "3", name: "Formazione Nord S.r.l." },
      { id: "4", name: "Consorzio Sud" },
    ] as StaticListItem[],
  },
  /** Campi aggiuntivi (modale categoria corso) */
  campi_aggiuntivi: {
    fallback: [
      { id: "profilo_professionale", name: "Profilo professionale" },
      { id: "titolo_studio", name: "Titolo di studio" },
      { id: "livello_esperienza", name: "Livello esperienza" },
      { id: "azienda", name: "Azienda" },
      { id: "note", name: "Note" },
    ] as StaticListItem[],
  },
};

/**
 * Verifica se una lista e' registrata
 */
export function isRegisteredList(listName: string): boolean {
  return listName in staticListRegistry;
}

/**
 * Ottiene la configurazione di una lista
 */
export function getListConfig(listName: string): StaticListConfig | null {
  return staticListRegistry[listName] ?? null;
}

/**
 * Lista di tutte le static list registrate (per debug/documentazione)
 */
export function getRegisteredLists(): string[] {
  return Object.keys(staticListRegistry);
}
